// Pure pricer-side handoff mapping (IM-1955): a PricerHandoffSpec -> an
// instrument seed (Partial<Instrument>) for the one-touch row the pricer
// creates. reconcileInstrument (in the provider) fills defaults and strips the
// unused barrier field; here we only place the barrier on the correct leg and
// carry the model / pay-type / expiry / notional through. Unit-tested in
// __tests__/handoff-seed.test.ts.
import type { Instrument } from './contracts';
import type { PricerHandoffSpec } from '../../providers/pricer-handoff/pricer-handoff-provider';

export function handoffSpecToInstrumentSeed(spec: PricerHandoffSpec): Partial<Instrument> {
  const common = {
    token: spec.token,
    contract: spec.contract,
    payType: spec.payType,
    notionalToken: spec.notionalToken,
    expiry: spec.expiry,
    notional: spec.notional,
    // Model pre-select: bs / lv / sv are all valid ModelKind and one_touch
    // prices under each (the MP board renders all three columns), so this is a
    // direct assignment — no fallback needed.
    model: spec.model,
  };
  // OneTouchUpper carries `upper`, OneTouchLower carries `lower`. Set only the
  // matching leg; reconcileInstrument strips whichever the schema omits.
  return (
    spec.contract === 'OneTouchUpper'
      ? { ...common, upper: spec.barrier }
      : { ...common, lower: spec.barrier }
  ) as Partial<Instrument>;
}

// Dedupe key for accumulate mode (IM-1955): does this existing basket
// instrument represent the SAME one-touch rung as the incoming spec? Matches on
// token + contract + barrier (on the correct leg) + expiry + payType, so a
// double-click on the same ladder cell doesn't stack a duplicate row.
export function isSameHandoffRung(inst: Instrument, spec: PricerHandoffSpec): boolean {
  const rec = inst as unknown as Record<string, unknown>;
  if (inst.contract !== spec.contract) return false;
  if (inst.token !== spec.token) return false;
  if (inst.expiry !== spec.expiry) return false;
  if (rec.payType !== spec.payType) return false;
  const barrier = spec.contract === 'OneTouchUpper' ? rec.upper : rec.lower;
  return barrier === spec.barrier;
}
